import { del, get, patch, post } from "@/services/axiosUitils";

const basePath = "/pts_app/change-request";

export type ChangeRequestPriority = "High" | "Medium" | "Low";

export type ChangeRequestStatus =
  | "Pending"
  | "On Hold"
  | "Reject"
  | "Approved"
  | string;

export interface ChangeRequestAttachment {
  url?: string;
  name?: string;
  file_path?: string;
  file_size?: number;
  file_type?: string;
  document_id?: string;
}

export interface ChangeRequestItem {
  id: string;
  cr_number: string;
  project: string;
  project_name?: string;
  title: string;
  priority: ChangeRequestPriority | string;
  requested_date: string;
  expected_completion_date?: string;
  assign_employees?: string[];
  estimated_hours?: number | string;
  description?: string;
  attachments?: ChangeRequestAttachment[];
  status: ChangeRequestStatus;
  ba?: string;
  ba_name?: string;
  cost?: number | string;
  billing_configured?: boolean | string;
  invoice_raised_status?: "yes" | "no" | boolean | string;
  raised_to_client_status?: string | null;
  raised_date?: string | null;
  google_sheet_link?: string | null;
  forwarded?: boolean;
  forwarded_to_accountant?: boolean;
  created_on?: string;
  updated_on?: string;
}

export interface CreateChangeRequestPayload {
  project: string;
  title: string;
  priority: ChangeRequestPriority;
  requested_date: string;
  expected_completion_date: string;
  assign_employees: string[];
  estimated_hours: string | number;
  description: string;
  raised_to_client_status?: string | null;
  raised_date?: string | null;
  google_sheet_link?: string | null;
  attachments?: File[];
}

export interface ImportGoogleSheetPayload {
  google_sheet_link: string;
}

export interface GoogleSheetLinkResponse {
  success?: {
    code?: string;
    message?: string;
  };
  id?: string;
  google_sheet_link?: string | null;
  updated_on?: string;
}

export interface ChangeRequestListParams {
  page?: number;
  page_size?: number;
  month?: string;
  year?: string | number;
  status?: string;
  project?: string;
  raised_to_client_status?: string;
  ba?: string;
}

export interface AccountChangeRequestFilters {
  month: string;
  year: number;
}

export interface AccountCumulativeProjectRow {
  project: string;
  project_name?: string;
  change_request?: number;
  total_hours?: number;
  total_cost?: number;
  invoice_raised_status?: "yes" | "no" | boolean | string;
}

export interface AccountChangeRequestCards {
  change_requests_this_month?: number;
  total_hours_this_month?: number;
  total_cost_this_month?: number;
}

export interface AccountChangeRequestResponse {
  success?: { code?: string; message?: string };
  filters?: AccountChangeRequestFilters;
  cards?: AccountChangeRequestCards;
  results?: AccountCumulativeProjectRow[];
  count?: number;
}

export interface UpdateAccountInvoicePayload {
  project: string;
  filters: AccountChangeRequestFilters;
  invoice_raised_status: boolean;
}

function unwrapList(response: unknown): ChangeRequestItem[] {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response as ChangeRequestItem[];
  }

  const data = response as Record<string, unknown>;
  if (Array.isArray(data.data)) {
    return data.data as ChangeRequestItem[];
  }
  if (Array.isArray(data.results)) {
    return data.results as ChangeRequestItem[];
  }
  if (Array.isArray(data.change_requests)) {
    return data.change_requests as ChangeRequestItem[];
  }

  return [];
}

class PtsChangeRequestService {
  /**
   * List change requests.
   * Calls: GET /pts_app/change-request/
   */
  getChangeRequests = async (
    params?: ChangeRequestListParams
  ): Promise<{ results: ChangeRequestItem[]; count: number }> => {
    const response = await get<unknown>(`${basePath}/`, { params });
    const results = unwrapList(response);

    let count = results.length;
    if (response && typeof response === "object" && !Array.isArray(response)) {
      const data = response as Record<string, unknown>;
      if (typeof data.count === "number") {
        count = data.count;
      } else if (typeof data.total === "number") {
        count = data.total;
      } else if (typeof data.total_count === "number") {
        count = data.total_count;
      }
    }

    return { results, count };
  };

  /**
   * Accountant dashboard: cards + cumulative by project.
   * Calls: GET /pts_app/change-request/account/?filters={"month":"August","year":2026}
   */
  getAccountChangeRequests = async (
    filters: AccountChangeRequestFilters
  ): Promise<AccountChangeRequestResponse> => {
    const response = await get<AccountChangeRequestResponse>(
      `${basePath}/account/`,
      {
        params: {
          filters: JSON.stringify(filters),
        },
      }
    );
    return response ?? { cards: {}, results: [], count: 0 };
  };

  /**
   * Update invoice raised status for a project in accountant view.
   * Calls: POST /pts_app/change-request/account/
   */
  updateAccountInvoiceRaised = async (
    payload: UpdateAccountInvoicePayload
  ): Promise<AccountChangeRequestResponse | undefined> => {
    return await post<AccountChangeRequestResponse>(
      `${basePath}/account/`,
      payload,
      { notifyMessage: true }
    );
  };

  /**
   * Create a change request (multipart/form-data).
   * Calls: POST /pts_app/change-request/
   */
  createChangeRequest = async (
    payload: CreateChangeRequestPayload
  ): Promise<ChangeRequestItem | undefined> => {
    const formData = this.buildFormData(payload);
    return await post<ChangeRequestItem>(`${basePath}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      notifyMessage: true,
    });
  };

  /**
   * Update a change request (multipart/form-data).
   * Calls: PATCH /pts_app/change-request/{id}/
   */
  updateChangeRequestForm = async (
    id: string,
    payload: CreateChangeRequestPayload
  ): Promise<ChangeRequestItem | undefined> => {
    const formData = this.buildFormData(payload);
    return await patch<ChangeRequestItem>(`${basePath}/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      notifyMessage: true,
    });
  };

  importGoogleSheet = async (
    payload: ImportGoogleSheetPayload
  ): Promise<GoogleSheetLinkResponse | undefined> => {
    return await post<GoogleSheetLinkResponse>(
      `${basePath}/google-sheet/`,
      payload,
      {
        notifyMessage: true,
      }
    );
  };

  getGoogleSheetLink = async (): Promise<
    GoogleSheetLinkResponse | undefined
  > => {
    return await get<GoogleSheetLinkResponse>(`${basePath}/google-sheet/`);
  };

  /**
   * Delete a change request.
   * Calls: DELETE /pts_app/change-request/{id}/
   */
  deleteChangeRequest = async (id: string): Promise<unknown> => {
    return await del(`${basePath}/${id}/`, { notifyMessage: true });
  };

  private buildFormData = (payload: CreateChangeRequestPayload): FormData => {
    const formData = new FormData();
    formData.append("project", payload.project);
    formData.append("title", payload.title);
    formData.append("priority", payload.priority);
    formData.append("requested_date", payload.requested_date);
    formData.append(
      "expected_completion_date",
      payload.expected_completion_date
    );
    formData.append("assign_employees", payload.assign_employees.join(","));
    formData.append("estimated_hours", String(payload.estimated_hours));
    formData.append("description", payload.description);
    formData.append(
      "raised_to_client_status",
      payload.raised_to_client_status ?? ""
    );
    formData.append("raised_date", payload.raised_date ?? "");
    formData.append("google_sheet_link", payload.google_sheet_link ?? "");

    (payload.attachments ?? []).forEach(file => {
      formData.append("attachments", file);
    });

    return formData;
  };

  /**
   * Update change request fields / status.
   * Calls: PATCH /pts_app/change-request/{id}/
   */
  updateChangeRequest = async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<ChangeRequestItem | undefined> => {
    return await patch<ChangeRequestItem>(`${basePath}/${id}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Bulk update change requests status.
   * Calls: PATCH /pts_app/change-request/bulk-status/
   */
  bulkUpdateStatus = async (
    ids: string[],
    status: ChangeRequestStatus
  ): Promise<unknown> => {
    return await patch(
      `${basePath}/bulk-status/`,
      { ids, status },
      { notifyMessage: true }
    );
  };

  approveChangeRequest = async (id: string) => {
    return this.updateChangeRequest(id, { status: "Approved" });
  };

  rejectChangeRequest = async (id: string) => {
    return this.updateChangeRequest(id, { status: "Rejected" });
  };

  forwardToAccountant = async (id: string) => {
    return this.updateChangeRequest(id, {
      forwarded_to_accountant: true,
    });
  };
}

const instance = new PtsChangeRequestService();
export default instance;
